// src/pages/auth/ResetPassword.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Form,
  Heading,
  Button,
  Image,
  Text,
} from "../../components/elements";
import IconField from "../../components/fields/IconField";
import Logo from "../../components/Logo";
import data from "../../data/master/forgot.json";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";

export default function ResetPassword() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  const navigate = useNavigate();
  const { token } = useParams();

  // Verify token on component mount
  useEffect(() => {
    const verifyToken = async () => {
      try {
        await axios.get(
          `${process.env.REACT_APP_BASE_URL}common/verify-reset-token/${token}`
        );
        setTokenValid(true);
        setVerifying(false);
      } catch (e) {
        setError("This password reset link is invalid or has expired.");
        setTokenValid(false);
        setVerifying(false);
      }
    };

    if (token) {
      verifyToken();
    } else {
      setError("Invalid reset link.");
      setVerifying(false);
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      setError("Please fill in all fields.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}common/reset-password/${token}`,
        { newPassword }
      );

      setSuccess(true);
      setError("");

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (e) {
      setError(
        e.response?.data?.message || "Failed to reset password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="mc-auth">
      <Image
        className="mc-auth-pattern"
        src={data?.pattern.src}
        alt={data?.pattern.alt}
      />
      <Box className="mc-auth-group">
        <Logo
          src={data?.logo.src}
          alt={data?.logo.alt}
          href={data?.logo.path}
          className="mc-auth-logo"
        />
        <Heading as="h4" className="mc-auth-title">
          Set New Password
        </Heading>

        <Form className="mc-auth-form">
          {verifying ? (
            <Box style={{ textAlign: "center", padding: "20px" }}>
              <Text>Verifying reset link...</Text>
            </Box>
          ) : success ? (
            <Box style={{ textAlign: "center", padding: "20px" }}>
              <Text style={{ color: "#4CAF50", fontSize: "18px", marginBottom: "15px" }}>
                ✓ Password Reset Successfully!
              </Text>
              <Text style={{ marginBottom: "20px" }}>
                Your password has been updated. Redirecting to login...
              </Text>
            </Box>
          ) : !tokenValid ? (
            <Box style={{ textAlign: "center", padding: "20px" }}>
              <Text className="mc-alert red" style={{ marginBottom: "20px" }}>
                {error}
              </Text>
              <Button
                className={`mc-auth-btn ${data?.button.fieldSize}`}
                onClick={() => navigate("/forgot-password")}
              >
                Request New Link
              </Button>
              <Button
                className={`mc-auth-btn ${data?.button.fieldSize}`}
                onClick={() => navigate("/login")}
                style={{ marginTop: "10px", backgroundColor: "#6c757d" }}
              >
                Back to Login
              </Button>
            </Box>
          ) : (
            <>
              {/* New Password */}
              <IconField
                icon="password"
                type="password"
                classes="h-sm"
                placeholder="New Password"
                passwordVisible={true}
                required
                name="newPassword"
                onChange={(e) => setNewPassword(e.target.value)}
                value={newPassword}
              />

              {/* Confirm Password */}
              <IconField
                icon="password"
                type="password"
                classes="h-sm"
                placeholder="Confirm New Password"
                passwordVisible={true}
                required
                name="confirmPassword"
                onChange={(e) => setConfirmPassword(e.target.value)}
                value={confirmPassword}
              />

              {error && <Text className="mc-alert red">{error}</Text>}

              <Text style={{ fontSize: "14px", color: "#666", marginBottom: "15px" }}>
                Password must be at least 8 characters long and contain uppercase, lowercase, and numbers.
              </Text>

              {/* Submit button */}
              <Button
                className={`mc-auth-btn ${data?.button.fieldSize}`}
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>

              {/* Cancel button */}
              <Button
                className={`mc-auth-btn ${data?.button.fieldSize}`}
                onClick={() => navigate("/login")}
                style={{ marginTop: "10px", backgroundColor: "#6c757d" }}
              >
                Cancel
              </Button>
            </>
          )}
        </Form>
      </Box>
    </Box>
  );
}
